import { objectToSnake } from 'ts-case-convert';

import {
  createAiServerRequestHeaders,
  extractServerName,
  getConfiguredAiServerBaseUrl,
} from '@/api/ai/requestConfig';
import { ApiTokenStorage } from '@/module/auth/utils/ApiTokenStorage';
import { getClientFingerprintId } from '@/shared/utils/fingerprint';

import { isRecord } from './isRecord';
import {
  type ServerAiBuilderChatConfirmDecision,
  type ServerAiBuilderChatEventDto,
  type ServerAiBuilderChatFeedbackResponseDto,
  type ServerAiBuilderChatHttpErrorDto,
  type ServerAiBuilderChatQuestionSnapshotDto,
  type ServerAiBuilderChatResetResponseDto,
  type ServerAiBuilderChatSessionResponseDto,
  type ServerAiBuilderChatSourceResponseDto,
  type ServerAiBuilderChatSourceType,
  type ServerAiBuilderChatUsageInfoDto,
  type ServerAiBuilderChatUsageState,
} from './serverAiBuilderChatDtos';
import { isServerAiBuilderChatErrorCode } from './serverAiBuilderChatErrorMessages';
import { parseServerAiBuilderChatSseStream } from './serverAiBuilderChatSseParser';
import { type AiBuilderTurnHints } from './types';

const SERVER_AI_BUILDER_CHAT_USAGE_STATES = [
  'normal',
  'low',
  'blocked',
] satisfies ServerAiBuilderChatUsageState[];

export type ServerAiBuilderChatFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export type ServerAiBuilderChatTransportDependencies = {
  baseUrl?: string;
  fetch?: ServerAiBuilderChatFetch;
  getToken?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
  getServerName?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
  getFingerprintId?: () =>
    | string
    | null
    | undefined
    | Promise<string | null | undefined>;
};

export type CreateServerAiBuilderChatSessionRequest = {
  locale: string;
  /** 유저 국가 코드 (ISO 3166-1 alpha-2) — 서버 성취기준 검색 게이트 판정 축. */
  countryCode: string;
  draftInstanceId: string;
};

export type StreamServerAiBuilderChatTurnRequest = {
  sessionId: string;
  draftInstanceId: string;
  clientRequestId: string;
  message?: string;
  action?: 'chat' | 'undo';
  signal?: AbortSignal;
  confirm?: {
    token: string;
    decision: ServerAiBuilderChatConfirmDecision;
  } | null;
  sourceRefs?: string[];
  builderContext: {
    userName: string;
    schoolLevel: string;
    grade: string;
    subject: string;
  };
  selectedQuestionContext?: {
    id: string;
    number: number;
    question: string;
  } | null;
  turnHints?: AiBuilderTurnHints;
  questions: ServerAiBuilderChatQuestionSnapshotDto[];
  questionListRevision: string;
};

export type ResetServerAiBuilderChatSessionRequest = {
  sessionId: string;
  draftInstanceId: string;
};

export type UpdateServerAiBuilderChatFeedbackRequest = {
  sessionId: string;
  draftInstanceId: string;
  traceId: string;
  feedback: 'good' | 'bad' | null;
};

export type RegisterServerAiBuilderChatSourceRequest =
  | {
      sessionId: string;
      draftInstanceId: string;
      sourceType: Extract<ServerAiBuilderChatSourceType, 'file'>;
      file: Blob;
      fileName?: string;
    }
  | {
      sessionId: string;
      draftInstanceId: string;
      sourceType: Exclude<ServerAiBuilderChatSourceType, 'file'>;
      url: string;
    };

export const createSession = async (
  request: CreateServerAiBuilderChatSessionRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<ServerAiBuilderChatSessionResponseDto> => {
  const response = await getFetch(dependencies)(
    createUrl('/conversation/session', dependencies),
    {
      method: 'POST',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
        contentType: 'application/json',
      }),
      body: JSON.stringify({
        locale: request.locale,
        country_code: request.countryCode,
        draft_instance_id: request.draftInstanceId,
      }),
    },
  );

  return parseJsonResponse<ServerAiBuilderChatSessionResponseDto>(response);
};

export const streamTurn = async (
  request: StreamServerAiBuilderChatTurnRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<AsyncIterable<ServerAiBuilderChatEventDto>> => {
  const response = await getFetch(dependencies)(
    createUrl('/conversation/turn', dependencies),
    {
      method: 'POST',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'text/event-stream',
        contentType: 'application/json',
      }),
      signal: request.signal,
      body: JSON.stringify(createTurnRequestBody(request)),
    },
  );

  if (!response.ok) {
    return createHttpErrorEventStream(response);
  }

  if (!response.body) {
    throw new Error('Server AI builder chat turn response body is empty.');
  }

  return parseServerAiBuilderChatSseStream(response.body);
};

export const resetSession = async (
  request: ResetServerAiBuilderChatSessionRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<ServerAiBuilderChatResetResponseDto> => {
  const response = await getFetch(dependencies)(
    createUrl(`/conversation/session/${request.sessionId}/reset`, dependencies),
    {
      method: 'POST',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
        contentType: 'application/json',
      }),
      body: JSON.stringify({
        draft_instance_id: request.draftInstanceId,
      }),
    },
  );

  return parseJsonResponse<ServerAiBuilderChatResetResponseDto>(response);
};

export const updateResponseFeedback = async (
  request: UpdateServerAiBuilderChatFeedbackRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<void> => {
  const response = await getFetch(dependencies)(
    createUrl('/conversation/feedback', dependencies),
    {
      method: 'PUT',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
        contentType: 'application/json',
      }),
      body: JSON.stringify({
        session_id: request.sessionId,
        draft_instance_id: request.draftInstanceId,
        trace_id: request.traceId,
        feedback: request.feedback,
      }),
    },
  );

  await parseJsonResponse<ServerAiBuilderChatFeedbackResponseDto>(response);
};

export const registerSource = async (
  request: RegisterServerAiBuilderChatSourceRequest,
  dependencies: ServerAiBuilderChatTransportDependencies = {},
): Promise<ServerAiBuilderChatSourceResponseDto> => {
  const response = await getFetch(dependencies)(
    createUrl(
      `/conversation/session/${request.sessionId}/source`,
      dependencies,
    ),
    {
      method: 'POST',
      credentials: 'include',
      headers: await createHeaders(dependencies, {
        accept: 'application/json',
      }),
      body: createSourceFormData(request),
    },
  );

  return parseJsonResponse<ServerAiBuilderChatSourceResponseDto>(response);
};

const createTurnRequestBody = (
  request: StreamServerAiBuilderChatTurnRequest,
) => {
  const body = objectToSnake({
    sessionId: request.sessionId,
    draftInstanceId: request.draftInstanceId,
    clientRequestId: request.clientRequestId,
    ...(request.message !== undefined ? { message: request.message } : {}),
    action: request.action ?? 'chat',
    confirm: request.confirm ?? null,
    sourceRefs: request.sourceRefs ?? [],
    builderContext: request.builderContext,
    ...(request.selectedQuestionContext
      ? { selectedQuestionContext: request.selectedQuestionContext }
      : {}),
    ...(request.turnHints ? { turnHints: request.turnHints } : {}),
    questionListRevision: request.questionListRevision,
  });

  return {
    ...body,
    questions: request.questions,
  };
};

const createSourceFormData = (
  request: RegisterServerAiBuilderChatSourceRequest,
) => {
  const formData = new FormData();
  formData.append('source_type', request.sourceType);
  formData.append('draft_instance_id', request.draftInstanceId);

  if (request.sourceType === 'file') {
    if (request.fileName) {
      formData.append('file', request.file, request.fileName);
    } else {
      formData.append('file', request.file);
    }
    return formData;
  }

  formData.append('url', request.url);
  return formData;
};

const parseJsonResponse = async <ResponseDto>(
  response: Response,
): Promise<ResponseDto> => {
  if (!response.ok) {
    throw createHttpError(response);
  }

  return (await response.json()) as ResponseDto;
};

async function* createHttpErrorEventStream(
  response: Response,
): AsyncIterable<ServerAiBuilderChatEventDto> {
  yield await createHttpErrorEvent(response);
}

const createHttpErrorEvent = async (
  response: Response,
): Promise<ServerAiBuilderChatEventDto> => {
  const parsedError = await parseHttpErrorDto(response);

  if (parsedError) {
    return {
      type: 'error',
      code: parsedError.code,
      message: parsedError.message,
      ...(parsedError.reason ? { reason: parsedError.reason } : {}),
      ...(parsedError.usage ? { usage: parsedError.usage } : {}),
    };
  }

  return createFallbackHttpErrorEvent();
};

const parseHttpErrorDto = async (
  response: Response,
): Promise<ServerAiBuilderChatHttpErrorDto | null> => {
  try {
    const body = (await response.json()) as unknown;
    const candidate = getHttpErrorCandidate(body);

    return normalizeServerAiBuilderChatHttpErrorDto(candidate);
  } catch {
    // Invalid or empty error bodies fall back to a generic error code.
  }

  return null;
};

const getHttpErrorCandidate = (body: unknown): unknown => {
  if (!isRecord(body) || !('detail' in body)) return body;

  return body['detail'];
};

const createFallbackHttpErrorEvent = (): ServerAiBuilderChatEventDto => ({
  type: 'error',
  code: 'INTERNAL_ERROR',
  message: 'INTERNAL_ERROR',
});

const normalizeServerAiBuilderChatHttpErrorDto = (
  value: unknown,
): ServerAiBuilderChatHttpErrorDto | null => {
  if (isServerAiBuilderChatErrorCode(value)) {
    return {
      code: value,
      message: value,
    };
  }

  if (!isRecord(value)) return null;
  if (!isServerAiBuilderChatErrorCode(value['code'])) return null;

  if (
    value['usage'] !== undefined &&
    !isServerAiBuilderChatUsageInfoDto(value['usage'])
  ) {
    return null;
  }

  return {
    code: value['code'],
    message:
      typeof value['message'] === 'string' ? value['message'] : value['code'],
    ...(typeof value['reason'] === 'string' && value['reason']
      ? { reason: value['reason'] }
      : {}),
    ...(value['usage'] ? { usage: value['usage'] } : {}),
  };
};

const isServerAiBuilderChatUsageInfoDto = (
  value: unknown,
): value is ServerAiBuilderChatUsageInfoDto => {
  if (!isRecord(value)) return false;

  return (
    isServerAiBuilderChatUsageState(value['state']) &&
    typeof value['remaining'] === 'number' &&
    typeof value['limit'] === 'number'
  );
};

const isServerAiBuilderChatUsageState = (
  value: unknown,
): value is ServerAiBuilderChatUsageState => {
  return (
    typeof value === 'string' &&
    SERVER_AI_BUILDER_CHAT_USAGE_STATES.includes(
      value as ServerAiBuilderChatUsageState,
    )
  );
};

const createHeaders = async (
  dependencies: ServerAiBuilderChatTransportDependencies,
  options: {
    accept?: string;
    contentType?: string;
  },
) => {
  const headers = new Headers();

  if (options.accept) {
    headers.set('Accept', options.accept);
  }

  if (options.contentType) {
    headers.set('Content-Type', options.contentType);
  }

  const token = await resolveDependencyValue(
    dependencies.getToken,
    ApiTokenStorage.get,
  );
  const serverName = await resolveDependencyValue(
    dependencies.getServerName,
    extractServerName,
  );

  let fingerprintId: string | null | undefined;
  try {
    fingerprintId = await resolveDependencyValue(
      dependencies.getFingerprintId,
      getClientFingerprintId,
    );
  } catch {
    // Fingerprint is best-effort and must not block the request.
  }

  const requestHeaders = createAiServerRequestHeaders({
    token,
    serverName,
    fingerprintId,
  });

  for (const [key, value] of Object.entries(requestHeaders)) {
    headers.set(key, value);
  }

  return headers;
};

const resolveDependencyValue = async <Value>(
  provider: (() => Value | Promise<Value>) | undefined,
  fallback: () => Value | Promise<Value>,
) => {
  if (provider) {
    return provider();
  }

  return fallback();
};

const getFetch = (
  dependencies: ServerAiBuilderChatTransportDependencies,
): ServerAiBuilderChatFetch => {
  if (dependencies.fetch) return dependencies.fetch;

  if (typeof globalThis.fetch !== 'function') {
    throw new Error('fetch is not available for server AI builder chat.');
  }

  return globalThis.fetch.bind(globalThis);
};

const createUrl = (
  path: string,
  dependencies: ServerAiBuilderChatTransportDependencies,
) => {
  const baseUrl = dependencies.baseUrl ?? getConfiguredAiServerBaseUrl();

  if (!baseUrl) {
    throw new Error(
      'NEXT_PUBLIC_AI_SERVER_BASEURL is required for server AI builder chat.',
    );
  }

  return new URL(path, baseUrl).toString();
};

const createHttpError = (response: Response) => {
  return new Error(
    `Server AI builder chat request failed with status ${response.status}.`,
  );
};
